import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { EmptyState } from '@/components/empty-state'
import { Favicon } from '@/components/favicon'
import { Pager } from '@/components/pager'
import { Panel } from '@/components/panel'
import { ScoreMeter } from '@/components/score-meter'
import { type GapRow, useLinkGap } from '@/lib/competitors'
import { bareUrl, formatExact } from '@/lib/format'
import { cn } from '@/lib/utils'
import { FilterInput, SectionHeader, TableSkeleton, useSiteScope } from '../../-section'

const PAGE_SIZE = 25

/** How many competitor marks fit in the column before they stop being
 * scannable. Past this the rest are a count, because a row of fourteen 14px
 * icons is a smear rather than an answer. */
const MAX_MARKS = 5

export const Route = createFileRoute('/_app/$org/$host/competitors/_list/')({
  component: LinkGapPage,
})

/**
 * Domains that link to this site's competitors and not to this site.
 *
 * The report the whole feed exists for. Their profiles on their own are two
 * lists of four thousand domains nobody works through; the difference between
 * them is a worklist, and the ordering is the answer — how many of them a
 * domain already links to, then how strong it is. So the columns are
 * deliberately not sortable, the way `low-hanging`'s are not: a sort control
 * would hand back the only job the page exists to do.
 *
 * A domain linking to one competitor is a coincidence. Three of three is a
 * domain that links to this kind of site, which is the finding.
 */
function LinkGapPage() {
  const { host, site } = useSiteScope()
  const [offset, setOffset] = useState(0)
  const [search, setSearch] = useState('')
  const { data, isPending, isFetching } = useLinkGap(site, { limit: PAGE_SIZE, offset, search })

  if (isPending && !data) {
    return (
      <>
        <h1 className="sr-only">Link gap</h1>
        <TableSkeleton />
      </>
    )
  }

  const total = data?.total ?? 0
  const competitors = data?.competitors ?? []
  const waiting = competitors.every((entry) => entry.lastSyncedAt === null)

  return (
    <>
      <SectionHeader
        title="Link gap"
        filter={
          total > 0 || search ? (
            <FilterInput
              placeholder="Filter domains"
              value={search}
              onChange={(next) => {
                setOffset(0)
                setSearch(next)
              }}
            />
          ) : undefined
        }
      />

      <Panel
        title="Linking to them, not to you"
        meta={total > 0 ? formatExact(total) : undefined}
        flush
        className={cn(isFetching && 'opacity-70 transition-opacity')}
      >
        {data && data.rows.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="w-9 py-2 pr-2 pl-3 text-right">
                      <span className="mono-label text-muted-foreground/70">#</span>
                    </th>
                    <th className="w-[28%] py-2 pr-3 text-left">
                      <span className="mono-label text-muted-foreground">Domain</span>
                    </th>
                    <th className="w-[26%] py-2 pr-3 text-left">
                      <span className="mono-label text-muted-foreground">Example link</span>
                    </th>
                    <th className="w-[96px] px-3 py-2 text-left">
                      <span className="mono-label text-muted-foreground">Links to</span>
                    </th>
                    <th className="w-[104px] px-3 py-2 text-right">
                      <span className="mono-label text-muted-foreground">Authority</span>
                    </th>
                    <th className="w-[92px] px-3 py-2 text-right">
                      <span className="mono-label text-muted-foreground">Spam</span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {data.rows.map((row, index) => (
                    <Row
                      key={row.id}
                      row={row}
                      rank={offset + index + 1}
                      bar={
                        competitors.length > 0 ? (row.competitors / competitors.length) * 100 : 0
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end border-t border-border px-3 py-2">
              <Pager offset={offset} limit={PAGE_SIZE} total={total} onOffsetChange={setOffset} />
            </div>
          </>
        ) : (
          <EmptyState
            title={waiting ? 'Collecting their links' : 'Nothing in the gap'}
            description={
              waiting
                ? 'Their profiles are being read. The first domains usually appear within a few minutes.'
                : search
                  ? 'No domain in the gap matches that filter.'
                  : `Every domain linking to them already links to ${host}.`
            }
          />
        )}
      </Panel>
    </>
  )
}

/**
 * One domain worth a link, on one line.
 *
 * The example is a column rather than a second line for the reason
 * `low-hanging` gives the ranking url one: it is what makes the row actionable
 * — a resource page, a roundup, a forum answer — and a footnote under the
 * domain would read as a caption rather than as the thing to look at.
 */
function Row({
  row,
  rank,
  bar,
}: {
  row: GapRow
  rank: number
  /** 0–100, how many of the competitors this domain links to. */
  bar: number
}) {
  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-secondary/40">
      <td className="py-2 pr-2 pl-3 text-right align-middle">
        <span className="font-mono text-[11px] text-muted-foreground/70 tabular">
          {String(rank).padStart(2, '0')}
        </span>
      </td>

      {/* The list is ordered by how many competitors a domain links to and
          cannot be re-sorted, so the bar behind the label is the ordering made
          visible — the same device the other opinionated table uses. */}
      <td className="relative py-2 pr-3">
        <span
          className="absolute inset-y-1 left-0 rounded-r-[2px] bg-plum/10"
          style={{ width: `${bar}%` }}
          aria-hidden
        />
        <span className="relative flex min-w-0 items-center gap-2">
          <Favicon host={row.domain} size={14} className="shrink-0 rounded-[3px]" />
          <a
            href={`https://${row.domain}`}
            target="_blank"
            rel="noreferrer noopener"
            className="min-w-0 truncate font-mono text-[12.5px] underline-offset-2 hover:underline"
            title={row.domain}
          >
            {row.domain}
          </a>
        </span>
      </td>

      <td className="max-w-0 py-2 pr-3">
        <ExampleCell row={row} />
      </td>

      {/* Which of them, as their own marks — the column has to say *which*, and
          three hosts as text truncate to one where three favicons do not. Past
          a handful it stops being scannable and becomes a smear, so the rest
          are counted rather than drawn; the title carries the full list. */}
      <td className="px-3 py-2">
        <span className="flex items-center gap-1" title={row.competitorHosts.join(', ')}>
          {row.competitorHosts.slice(0, MAX_MARKS).map((competitorHost) => (
            <Favicon
              key={competitorHost}
              host={competitorHost}
              size={14}
              className="shrink-0 rounded-[3px]"
            />
          ))}
          {row.competitorHosts.length > MAX_MARKS && (
            <span className="font-mono text-[11px] text-muted-foreground tabular">
              +{row.competitorHosts.length - MAX_MARKS}
            </span>
          )}
        </span>
      </td>

      <td className="px-3 py-2 text-right align-middle">
        <ScoreMeter kind="authority" value={row.rank} />
      </td>

      <td className="px-3 py-2 text-right align-middle">
        <ScoreMeter kind="spam" value={row.spamScore} />
      </td>
    </tr>
  )
}

/**
 * How the link was earned, as far as one link can say.
 *
 * The anchor sits under the url because the two answer different halves: the
 * url is the kind of page that links out, and the anchor is what it called
 * them. A row with no example renders nothing rather than a placeholder — a
 * change that cannot be computed renders nothing, and an example nobody bought
 * is the same claim.
 */
function ExampleCell({ row }: { row: GapRow }) {
  if (!row.exampleUrlFrom) {
    return <span className="font-mono text-[11.5px] text-muted-foreground/50">—</span>
  }

  return (
    <div className="min-w-0">
      <a
        href={row.exampleUrlFrom}
        target="_blank"
        rel="noreferrer noopener"
        className="block truncate font-mono text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        title={row.exampleUrlFrom}
      >
        {bareUrl(row.exampleUrlFrom)}
      </a>
      {row.exampleAnchor && (
        <span
          className="block truncate text-[11.5px] text-muted-foreground/70"
          title={row.exampleAnchor}
        >
          “{row.exampleAnchor}”
        </span>
      )}
    </div>
  )
}
